import { useState } from "react";
import { useTranslation } from "react-i18next";

import { useAuth } from "@/lib/auth/auth-context";
import { formatGroupNames } from "@/lib/format/group-names";
import { useAutosave } from "@/lib/hooks/useAutosave";
import { SaveStatus } from "@/components/SaveStatus";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Switch } from "@/components/ui/switch";

import { saveMyConsent, useMyConsentQuery } from "../api";
import type { Consent } from "../types";

/**
 * The member-controlled consent flags, in display order. Progress consent is
 * intentionally absent: it's an always-on baseline set server-side while the
 * member belongs to a group (mirrors the legacy consent page), never a toggle.
 */
type ConsentFlagKey = "consent_diagnostic_at" | "consent_edit_at";
const FLAGS: { key: ConsentFlagKey; i18n: string }[] = [
  { key: "consent_diagnostic_at", i18n: "diagnostic" },
  { key: "consent_edit_at", i18n: "edit" },
];

type Flags = Record<ConsentFlagKey, boolean>;

function toFlags(consent: Consent): Flags {
  return {
    consent_diagnostic_at: consent.consent_diagnostic_at != null,
    consent_edit_at: consent.consent_edit_at != null,
  };
}

function ConsentForm({ consent }: { consent: Consent }) {
  const { t } = useTranslation();
  const { user } = useAuth();
  // Name the member's group(s) in the intro copy (K-4). Sourced from /auth/me
  // (already loaded) so no extra fetch. Empty → hide the intro, like Django's
  // `{% if group_names_display %}`.
  const groupNames = user?.partner_group_names ?? [];
  const groups = formatGroupNames(groupNames, t("consents.member.and"));
  const [flags, setFlags] = useState<Flags>(() => toFlags(consent));

  const autosave = useAutosave<Flags>({
    // Only the two member-writable flags are sent; consent_progress_at is
    // read-only server-side (the always-on baseline).
    save: (values, signal) =>
      saveMyConsent(
        {
          consent_diagnostic_at: values.consent_diagnostic_at
            ? new Date().toISOString()
            : null,
          consent_edit_at: values.consent_edit_at
            ? new Date().toISOString()
            : null,
        },
        signal,
      ),
    debounceMs: 400,
  });

  const toggle = (key: ConsentFlagKey, next: boolean) => {
    setFlags((prev) => {
      const updated = { ...prev, [key]: next };
      autosave.save(updated);
      return updated;
    });
  };

  return (
    <Card>
      <CardHeader className="flex-row items-start justify-between">
        <CardTitle>{t("consents.member.heading")}</CardTitle>
        <SaveStatus status={autosave.status} />
      </CardHeader>
      <CardContent className="space-y-4">
        {groupNames.length > 0 && (
          <div className="space-y-2 text-sm text-muted-foreground">
            <p>{t("consents.member.intro", { count: groupNames.length, groups })}</p>
            <p>{t("consents.member.introOptional", { groups })}</p>
          </div>
        )}
        <ul className="divide-y border-t">
          {FLAGS.map(({ key, i18n }) => (
            <li key={key} className="flex items-start justify-between gap-4 py-4">
              <div>
                <p className="font-medium text-foreground">
                  {t(`consents.member.${i18n}`)}
                </p>
                <p
                  className={
                    flags[key]
                      ? "mt-1 text-xs font-semibold text-success"
                      : "mt-1 text-xs text-muted-foreground"
                  }
                >
                  {flags[key]
                    ? t("consents.member.granted")
                    : t("consents.member.notGranted")}
                </p>
              </div>
              <Switch
                checked={flags[key]}
                onCheckedChange={(next) => toggle(key, next)}
                label={t(`consents.member.${i18n}`)}
              />
            </li>
          ))}
        </ul>
      </CardContent>
    </Card>
  );
}

/** Member view: grant/revoke what the partner group may consult/edit (autosaved). */
export function MemberConsent() {
  const { t } = useTranslation();
  const query = useMyConsentQuery();

  if (query.isLoading) {
    return <p className="text-sm text-muted-foreground">{t("consents.loading")}</p>;
  }

  // A 404 here means the member has no enterprise profile yet.
  if (query.isError || !query.data) {
    return (
      <Card>
        <CardContent className="py-6">
          <p className="text-sm text-muted-foreground">
            {t("consents.member.noProfile")}
          </p>
          <Button
            variant="outline"
            size="sm"
            className="mt-3"
            onClick={() => void query.refetch()}
          >
            {t("consents.retry")}
          </Button>
        </CardContent>
      </Card>
    );
  }

  return <ConsentForm consent={query.data} />;
}
